import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Form, Table, Button, Spinner, Alert, InputGroup } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import { adminApi, extractError } from '../api/client';
import { fmtRelative, fmtDate } from '../lib/lookups';
import { JOB_STATUSES, jobStatusMeta, targetTypeLabel } from '../lib/importMeta';

export default function ImportJobsListPage() {
  const { stateId } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [status, setStatus] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sources, setSources] = useState([]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => {
    adminApi.get(`/admin/states/${stateId}`).then((r) => setState(r.data)).catch(() => {});
    adminApi.get(`/admin/states/${stateId}/data-sources?page=1&page_size=200`)
      .then((r) => setSources(r.data.items || []))
      .catch(() => {});
  }, [stateId]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = { page, page_size: pageSize };
      if (status) params.status = status;
      if (sourceFilter) params.data_source_id = sourceFilter;
      const res = await adminApi.get(`/admin/states/${stateId}/import-jobs`, { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) { setError(extractError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stateId, page, status, sourceFilter]);

  function sourceNameOf(id) {
    return sources.find((s) => s.id === id)?.name || id?.slice(0, 8) + '…';
  }

  return (
    <>
      <PageHeader
        title="Import Jobs"
        subtitle={state ? `${total} job${total === 1 ? '' : 's'} · ${state.name} (${state.abbreviation})` : `${total} jobs`}
        breadcrumb={
          <span>
            <Link to="/admin/states" className="text-decoration-none">States</Link>
            {' / '}{state && <Link to={`/admin/states/${stateId}`} className="text-decoration-none">{state.name}</Link>}
            {' / '}<Link to={`/admin/states/${stateId}/data-management`} className="text-decoration-none">Data Management</Link>
            {' / '}Import Jobs
          </span>
        }
        actions={
          <Link to={`/admin/states/${stateId}/data-sources`}>
            <Button variant="outline-secondary" data-testid="import-jobs-back-to-sources-button"><i className="bi bi-link-45deg me-1" /> Data Sources</Button>
          </Link>
        }
      />
      <div className="page-content">
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-2">
              <Col xs={12} md={4}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Status</Form.Label>
                <Form.Select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} data-testid="import-jobs-status-select">
                  <option value="">All</option>
                  {JOB_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Data source</Form.Label>
                <Form.Select value={sourceFilter} onChange={(e) => { setPage(1); setSourceFilter(e.target.value); }} data-testid="import-jobs-source-select">
                  <option value="">All sources</option>
                  {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}

        <Card>
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-clock-history" />
              <div>No import jobs yet.</div>
              <Link to={`/admin/states/${stateId}/data-sources`}>
                <Button variant="primary" className="mt-2">Go to data sources</Button>
              </Link>
            </div>
          ) : (
            <>
              <Table hover responsive size="sm" className="mb-0" data-testid="import-jobs-table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Data source</th>
                    <th>Target type</th>
                    <th>Status</th>
                    <th>Extracted</th>
                    <th>Staged</th>
                    <th>Applied</th>
                    <th>Finished</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((j) => {
                    const meta = jobStatusMeta(j.status);
                    return (
                      <tr key={j.id} data-testid={`import-job-row-${j.id}`} onClick={() => navigate(`/admin/states/${stateId}/import-jobs/${j.id}`)} style={{ cursor: 'pointer' }}>
                        <td>
                          <div className="fw-semibold">{j.started_at ? fmtRelative(j.started_at) : '—'}</div>
                          <div className="text-muted" style={{ fontSize: '0.78rem' }}>{j.started_at ? fmtDate(j.started_at) : ''}</div>
                        </td>
                        <td>{j.data_source_name || sourceNameOf(j.data_source_id)}</td>
                        <td>{targetTypeLabel(j.target_type)}</td>
                        <td><span className={`status-badge ${meta.badge}`}>{meta.label}</span></td>
                        <td>{j.items_extracted_count}</td>
                        <td>{j.items_staged_count}</td>
                        <td>{j.items_applied_count}</td>
                        <td className="text-muted">{j.finished_at ? fmtRelative(j.finished_at) : '—'}</td>
                        <td className="text-end">
                          <Link to={`/admin/states/${stateId}/import-jobs/${j.id}`} onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="outline-primary" data-testid={`import-job-view-${j.id}`}>Open</Button>
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3" style={{ borderTop: '1px solid var(--border)' }}>
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>Page {page} of {totalPages} · {total} jobs</div>
                  <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </>
  );
}
